import React, { useState, useEffect } from 'react';
import {
  Table, Button, Checkbox, Tag, Space, Divider, Typography, Spin, Empty, Row, Col
} from 'antd';
import { SaveOutlined, UserOutlined } from '@ant-design/icons';
import { useQueryClient } from 'react-query';
import {
  useListarRoles,
  usePermisosDeRol,
  useTodosLosPermisos,
  useAsignarPermisos,
} from '../../../Hooks/Admin';

const { Text, Title } = Typography;

const SECCIONES = [
  { key: 'configuracion', label: 'Configuración' },
  { key: 'objetos', label: 'Objetos' },
  { key: 'zonas', label: 'Zonas / Gachapon' },
];

const ACCIONES = [
  { key: 'read',   label: 'Ver' },
  { key: 'create', label: 'Crear' },
  { key: 'edit',   label: 'Editar' },
  { key: 'delete', label: 'Eliminar' },
];

export const RolesYPermisosTab = () => {
  const queryClient = useQueryClient();

  // Roles
  const { data: rolesData, isLoading: loadingRoles } = useListarRoles({ perPage: 100 });
  const roles = rolesData?.data?.data ?? rolesData?.data ?? [];

  // Todos los permisos (para construir la tabla)
  const { data: todosPermisos = [], isLoading: loadingPermisos } = useTodosLosPermisos();

  // Rol seleccionado
  const [selectedRolId, setSelectedRolId] = useState(null);
  const [checked, setChecked] = useState({}); // { [perm_id]: boolean }

  // Permisos del rol seleccionado
  const { data: rolData, isLoading: loadingRolPermisos } = usePermisosDeRol(selectedRolId);
  const asignar = useAsignarPermisos();

  // Cuando cambia el rol seleccionado, inicializar checkboxes
  useEffect(() => {
    if (!rolData) return;
    const permisosAsignados = rolData.permisos ?? [];
    const newChecked = {};
    (todosPermisos?.datos ?? todosPermisos ?? []).forEach((p) => {
      newChecked[p.id] = permisosAsignados.some((pa) => pa.id === p.id);
    });
    setChecked(newChecked);
  }, [rolData, todosPermisos]);

  const permisosList = todosPermisos?.datos ?? todosPermisos ?? [];

  // Buscar permiso id dado sección + acción
  const getPermisoId = (seccion, accion) => {
    const p = permisosList.find(
      (x) => x.perm_seccion === seccion && x.perm_accion === accion
    );
    return p?.id ?? null;
  };

  const handleCheck = (permisoId, value) => {
    setChecked((prev) => ({ ...prev, [permisoId]: value }));
  };

  const handleGuardar = () => {
    const permIds = Object.entries(checked)
      .filter(([, v]) => v)
      .map(([k]) => parseInt(k));
    asignar.mutate({ rol_id: selectedRolId, perm_ids: permIds }, {
      onSuccess: () => {
        queryClient.invalidateQueries(['permisosDeRol', selectedRolId]);
      }
    });
  };

  const selectedRol = roles.find((r) => r.id === selectedRolId);

  // Columnas de la tabla de roles
  const columnsRoles = [
    {
      title: 'Rol',
      dataIndex: 'role_nombre',
      key: 'role_nombre',
      render: (text, record) => (
        <Button
          type={selectedRolId === record.id ? 'primary' : 'link'}
          icon={<UserOutlined />}
          onClick={() => setSelectedRolId(record.id)}
          style={{ padding: '0 8px' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Permisos asignados',
      key: 'permisos',
      render: (_, record) => {
        const count = record.permisos_count ?? '—';
        return <Tag color="blue">{count} permisos</Tag>;
      },
    },
  ];

  return (
    <Row gutter={[24, 16]}>
      {/* Panel izquierdo: lista de roles */}
      <Col xs={24} md={8}>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Roles del sistema</Text>
        </div>
        <Table
          dataSource={roles}
          columns={columnsRoles}
          rowKey="id"
          loading={loadingRoles}
          pagination={false}
          size="small"
          rowClassName={(record) =>
            record.id === selectedRolId ? 'ant-table-row-selected' : ''
          }
          locale={{ emptyText: <Empty description="Sin roles" /> }}
        />
      </Col>

      {/* Panel derecho: matriz de permisos */}
      <Col xs={24} md={16}>
        {!selectedRolId ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#999' }}>
            <UserOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
            <Text type="secondary">Selecciona un rol para gestionar sus permisos</Text>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={5} style={{ margin: 0 }}>
                Permisos de: <Tag color="blue">{selectedRol?.role_nombre}</Tag>
              </Title>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleGuardar}
                loading={asignar.isLoading}
              >
                Guardar permisos
              </Button>
            </div>

            <Spin spinning={loadingRolPermisos || loadingPermisos}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Sección</th>
                    {ACCIONES.map((a) => (
                      <th key={a.key} style={{ ...thStyle, textAlign: 'center' }}>{a.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SECCIONES.map((s, i) => (
                    <tr key={s.key} style={{ background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                      <td style={tdStyle}>
                        <Text strong>{s.label}</Text>
                      </td>
                      {ACCIONES.map((a) => {
                        const pid = getPermisoId(s.key, a.key);
                        return (
                          <td key={a.key} style={{ ...tdStyle, textAlign: 'center' }}>
                            {pid ? (
                              <Checkbox
                                checked={!!checked[pid]}
                                onChange={(e) => handleCheck(pid, e.target.checked)}
                              />
                            ) : (
                              <Text type="secondary">—</Text>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Spin>
          </>
        )}
      </Col>
    </Row>
  );
};

const thStyle = {
  padding: '10px 12px',
  borderBottom: '2px solid #f0f0f0',
  background: '#fafafa',
  fontWeight: 600,
  textAlign: 'left',
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #f0f0f0',
};

export default RolesYPermisosTab;
